import type {
  EventFieldView,
  EventSectionView,
  EventSubmitRequestBody,
  EventSubmitResult,
  EventSubmitSection,
  EventView,
} from "common";
import type { EventPathEntry } from "./event-path";

export type FieldValue = string | boolean;

export interface ClientError {
  field: string;
  message: string;
}

export interface InstanceState {
  /** The picked record, or the record a previous submit saved. */
  recordId: string | null;
  values: Record<string, FieldValue>;
  /** The values as loaded from the picked record, to tell a cleared field from an empty one. */
  loaded: Record<string, FieldValue>;
  touched: boolean;
  /** True once this form inserted `recordId`; false for a picked or preselected record. */
  inserted: boolean;
  result: EventSubmitResult | null;
  clientError: ClientError | null;
}

export interface EventFormState {
  version: string | null;
  /** A collection id from the conditional dropdown, or null for none chosen. */
  dropdownChoice: string | null;
  sections: Record<string, InstanceState[]>;
  /** Keys of sections that save into an earlier section's record. */
  sharedKeys: string[];
}

export type EventFormAction =
  | {
      type: "setValue";
      key: string;
      instance: number;
      field: string;
      value: FieldValue;
    }
  | {
      type: "pickRecord";
      key: string;
      instance: number;
      recordId: string;
      values: Record<string, FieldValue>;
    }
  /** Drops the record and resets the instance to `values` (the section defaults). */
  | {
      type: "clearRecord";
      key: string;
      instance: number;
      values: Record<string, FieldValue>;
    }
  /**
   * A shared section shows its owner's record. `loaded` is the owner's row read for this
   * section, or `{}` when the row is unknown; `defaults` are the section defaults. An
   * untouched instance takes the row; a touched one keeps the fields the user changed and
   * takes the row for the rest; one with a result (a retry still owed) is left as it is.
   */
  | {
      type: "loadShared";
      key: string;
      instance: number;
      loaded: Record<string, FieldValue>;
      defaults: Record<string, FieldValue>;
    }
  /** `values` are the new instance's starting values; pass the section defaults. */
  | { type: "addInstance"; key: string; values?: Record<string, FieldValue> }
  | { type: "removeInstance"; key: string }
  | { type: "chooseDropdown"; collectionId: string | null }
  | { type: "applyResults"; results: EventSubmitResult[] }
  | { type: "setClientErrors"; errors: Record<string, (ClientError | null)[]> }
  | { type: "reset"; state: EventFormState };

export const REQUIRED_MESSAGE = "This field is required.";
export const JSON_MESSAGE = "Enter valid JSON.";
export const NUMBER_MESSAGE = "Enter a number.";

const isBoolean = (f: EventFieldView) => f.type === "Boolean";

/** The starting values of a new, empty instance of a section. */
export const defaultsOf = (
  section: EventSectionView
): Record<string, FieldValue> =>
  Object.fromEntries(
    section.fields.map(f => [
      f.name,
      isBoolean(f) ? f.default === "true" : (f.default ?? ""),
    ])
  );

const emptyInstance = (values: Record<string, FieldValue>): InstanceState => ({
  recordId: null,
  values,
  loaded: {},
  touched: false,
  inserted: false,
  result: null,
  clientError: null,
});

const hasAny = (values: Record<string, FieldValue>) =>
  Object.keys(values).length > 0;

/** A touched shared instance moved onto a newly loaded owner row, keeping the user's changes. */
const mergeShared = (
  current: InstanceState,
  loaded: Record<string, FieldValue>,
  defaults: Record<string, FieldValue>
): Record<string, FieldValue> => {
  const before = hasAny(current.loaded) ? current.loaded : defaults;
  const after = hasAny(loaded) ? loaded : defaults;
  const names = new Set([
    ...Object.keys(after),
    ...Object.keys(current.values),
  ]);
  return Object.fromEntries(
    [...names].map(name => [
      name,
      current.values[name] !== before[name]
        ? current.values[name]
        : after[name],
    ])
  );
};

export const initialFormState = (
  view: EventView,
  path: EventPathEntry[]
): EventFormState => {
  const sections: Record<string, InstanceState[]> = {};
  // The path record goes to the first section of its collection that can update it, else to
  // the first one that can neither insert nor update, which shows it read only (#1160). An
  // insert only section stays free for a new record.
  const holder = new Map<string, string>();
  for (const pass of [
    (s: EventSectionView) => s.update,
    (s: EventSectionView) => !s.insert,
  ]) {
    for (const s of view.sections) {
      if (s.sharesRecordWith !== null || holder.has(s.collectionName)) continue;
      if (pass(s)) holder.set(s.collectionName, s.key);
    }
  }
  for (const section of view.sections) {
    const entry = path.find(p => p.collection === section.collectionName);
    if (entry && holder.get(section.collectionName) === section.key) {
      // No defaults on an existing record: only its loaded values are ever sent back.
      sections[section.key] = [
        { ...emptyInstance({}), recordId: entry.recordId, touched: true },
      ];
    } else {
      sections[section.key] = [emptyInstance(defaultsOf(section))];
    }
  }
  return {
    version: view.version,
    dropdownChoice: null,
    sections,
    sharedKeys: view.sections
      .filter(s => s.sharesRecordWith !== null)
      .map(s => s.key),
  };
};

const updateInstance = (
  state: EventFormState,
  key: string,
  instance: number,
  change: (current: InstanceState) => InstanceState
): EventFormState => {
  const list = state.sections[key];
  if (!list || !list[instance]) return state;
  const next = list.slice();
  next[instance] = change(list[instance]);
  return { ...state, sections: { ...state.sections, [key]: next } };
};

/** Blank values shaped like an existing instance, for a caller that passes no defaults. */
const blankLike = (template: InstanceState): Record<string, FieldValue> =>
  Object.fromEntries(
    Object.entries({ ...template.loaded, ...template.values }).map(([k, v]) => [
      k,
      typeof v === "boolean" ? false : "",
    ])
  );

export const eventFormReducer = (
  state: EventFormState,
  action: EventFormAction
): EventFormState => {
  switch (action.type) {
    case "setValue":
      return updateInstance(state, action.key, action.instance, current => ({
        ...current,
        values: { ...current.values, [action.field]: action.value },
        touched: true,
        clientError: null,
      }));
    case "pickRecord":
      return updateInstance(state, action.key, action.instance, current => ({
        ...current,
        recordId: action.recordId,
        values: { ...current.values, ...action.values },
        loaded: action.values,
        touched: true,
        inserted:
          current.recordId === action.recordId ? current.inserted : false,
        // The picker reloads a record after a save; keep that save's result on the card.
        result: current.recordId === action.recordId ? current.result : null,
        clientError: null,
      }));
    case "clearRecord":
      return updateInstance(state, action.key, action.instance, () =>
        emptyInstance(action.values)
      );
    case "loadShared":
      // A shared section never holds a record id; it follows its owner's record.
      return updateInstance(state, action.key, action.instance, current => {
        if (!current.touched) {
          return {
            ...current,
            values: hasAny(action.loaded) ? action.loaded : action.defaults,
            loaded: action.loaded,
            clientError: null,
          };
        }
        // A retry still owes this input: never replace it.
        if (current.result) return current;
        return {
          ...current,
          values: mergeShared(current, action.loaded, action.defaults),
          loaded: action.loaded,
        };
      });
    case "addInstance": {
      const list = state.sections[action.key] ?? [];
      const template = list[0];
      if (!template) return state;
      const fresh = emptyInstance(action.values ?? blankLike(template));
      return {
        ...state,
        sections: { ...state.sections, [action.key]: [...list, fresh] },
      };
    }
    case "removeInstance": {
      const list = state.sections[action.key] ?? [];
      const last = list[list.length - 1];
      // Never drop the first instance or one that already has a saved record.
      if (list.length < 2 || last.recordId) return state;
      return {
        ...state,
        sections: { ...state.sections, [action.key]: list.slice(0, -1) },
      };
    }
    case "chooseDropdown":
      return { ...state, dropdownChoice: action.collectionId };
    case "applyResults": {
      let next = state;
      for (const r of action.results) {
        // A shared section saves into its owner's record and never holds an id of its own.
        const shared = state.sharedKeys.includes(r.section);
        next = updateInstance(next, r.section, r.instance, current => ({
          ...current,
          result: r,
          // An owned section saved with no record before it was an insert by this form.
          inserted:
            current.inserted ||
            (r.status === "saved" && !shared && !current.recordId),
          recordId:
            r.status === "saved" && r.recordId && !shared
              ? r.recordId
              : current.recordId,
          loaded: r.status === "saved" ? { ...current.values } : current.loaded,
        }));
      }
      return next;
    }
    case "setClientErrors": {
      const sections = Object.fromEntries(
        Object.entries(state.sections).map(([key, list]) => [
          key,
          list.map((inst, i) => ({
            ...inst,
            clientError: action.errors[key]?.[i] ?? null,
          })),
        ])
      );
      return { ...state, sections };
    }
    case "reset":
      return action.state;
  }
};

export const visibleSections = (
  view: EventView,
  state: EventFormState
): EventSectionView[] =>
  view.sections.filter(
    s => !s.hiddenByDropdown || s.collectionId === state.dropdownChoice
  );

/** The mode of a section that owns its record. Use sharedSectionMode for a shared one. */
export const sectionMode = (
  section: EventSectionView,
  instance: InstanceState
): "insert" | "update" | "read_only" => {
  if (instance.recordId) return section.update ? "update" : "read_only";
  return section.insert ? "insert" : "read_only";
};

/** The collection a reference field points at, or null for a ref outside this canvas. */
export const refCollectionName = (
  canvasId: string,
  ref: string | null
): string | null => {
  const prefix = `${canvasId}_`;
  return ref && ref.startsWith(prefix) ? ref.slice(prefix.length) : null;
};

const instanceOf = (
  state: EventFormState,
  section: EventSectionView,
  instance: number
): InstanceState | undefined => {
  const list = state.sections[section.key] ?? [];
  // A section that is not multiple has one instance that serves every repeat below it.
  return section.multiple ? list[instance] : list[0];
};

/**
 * The mode of a section that saves into its owner's record. It is sent with its owner at the
 * same instance, so it is read only when it cannot update, when the owner cannot save, or
 * when the owner has no such instance (a repeat past the first under a single owner).
 */
export const sharedSectionMode = (
  view: EventView,
  state: EventFormState,
  section: EventSectionView,
  instance: number
): "update" | "read_only" => {
  const owner = visibleSections(view, state).find(
    s => s.key === section.sharesRecordWith
  );
  if (!section.update || !owner) return "read_only";
  if (!owner.multiple && instance !== 0) return "read_only";
  const ownerInst = state.sections[owner.key]?.[instance];
  if (!ownerInst || sectionMode(owner, ownerInst) === "read_only") {
    return "read_only";
  }
  return "update";
};

/** The earlier visible, non shared section on the collection a reference field points at. */
export const linkSource = (
  view: EventView,
  state: EventFormState,
  section: EventSectionView,
  field: EventFieldView
): EventSectionView | null => {
  const target = refCollectionName(view.canvasId, field.ref);
  if (!target) return null;
  const visible = visibleSections(view, state);
  const index = visible.findIndex(s => s.key === section.key);
  return (
    visible
      .slice(0, Math.max(index, 0))
      .find(s => s.collectionName === target && s.sharesRecordWith === null) ??
    null
  );
};

export const parentFilter = (
  view: EventView,
  state: EventFormState,
  section: EventSectionView,
  instance: number
): { field: string; recordId: string } | null => {
  for (const f of section.fields) {
    const source = linkSource(view, state, section, f);
    const recordId = source
      ? instanceOf(state, source, instance)?.recordId
      : null;
    if (recordId) return { field: f.name, recordId };
  }
  return null;
};

const idOf = (value: unknown): string =>
  value && typeof value === "object" && "_id" in value
    ? String((value as { _id: unknown })._id)
    : String(value);

export const recordValuesFromRow = (
  section: EventSectionView,
  row: Record<string, unknown>
): Record<string, FieldValue> =>
  Object.fromEntries(
    section.fields.map(f => {
      const v = row[f.name];
      if (isBoolean(f)) return [f.name, v === true];
      if (v === undefined || v === null) return [f.name, ""];
      if (f.ref) return [f.name, idOf(v)];
      if (f.type === "Date") return [f.name, String(v).slice(0, 10)];
      if (f.type === "Mixed" || typeof v === "object") {
        return [f.name, JSON.stringify(v, null, 2)];
      }
      return [f.name, String(v)];
    })
  );

const isEmpty = (v: FieldValue | undefined) => v === undefined || v === "";

/** The value as sent: number text is trimmed, so blank space is empty rather than 0. */
const fieldValue = (
  f: EventFieldView,
  v: FieldValue | undefined
): FieldValue | undefined =>
  f.type === "Number" && typeof v === "string" ? v.trim() : v;

const coerce = (
  f: EventFieldView,
  v: FieldValue
): { ok: true; value: unknown } | { ok: false; message: string } => {
  if (isBoolean(f)) return { ok: true, value: v === true };
  const text = String(v);
  if (f.type === "Number") {
    const n = Number(text);
    return Number.isFinite(n)
      ? { ok: true, value: n }
      : { ok: false, message: NUMBER_MESSAGE };
  }
  if (f.type === "Mixed") {
    try {
      return { ok: true, value: JSON.parse(text) };
    } catch {
      return { ok: false, message: JSON_MESSAGE };
    }
  }
  return { ok: true, value: text };
};

interface PlannedEntry {
  section: EventSectionView;
  instance: number;
  state: InstanceState;
  mode: "insert" | "update";
}

const entryId = (key: string, i: number) => `${key}#${i}`;

/** The instances a submit sends, in definition order then by instance. */
const plan = (view: EventView, state: EventFormState): PlannedEntry[] => {
  const visible = visibleSections(view, state);
  const chosen = new Set<string>();

  for (const s of visible) {
    (state.sections[s.key] ?? []).forEach((inst, i) => {
      if (!inst.touched) return;
      if (s.sharesRecordWith === null) {
        if (sectionMode(s, inst) !== "read_only") chosen.add(entryId(s.key, i));
        return;
      }
      if (sharedSectionMode(view, state, s, i) === "read_only") return;
      // The server accepts a shared section only beside its owner at the same instance.
      chosen.add(entryId(s.key, i));
      chosen.add(entryId(s.sharesRecordWith, i));
    });
  }

  const out: PlannedEntry[] = [];
  for (const s of visible) {
    (state.sections[s.key] ?? []).forEach((inst, i) => {
      if (!chosen.has(entryId(s.key, i))) return;
      const mode =
        s.sharesRecordWith !== null
          ? "update"
          : (sectionMode(s, inst) as "insert" | "update");
      out.push({ section: s, instance: i, state: inst, mode });
    });
  }
  return out;
};

/**
 * Whether the server will fill an empty reference field: its link source already has a
 * record (picked, or saved by an earlier attempt) or is saved in this same submit.
 */
const willLink = (
  view: EventView,
  state: EventFormState,
  planned: Set<string>,
  entry: PlannedEntry,
  f: EventFieldView
): boolean => {
  const source = linkSource(view, state, entry.section, f);
  if (!source) return false;
  if (instanceOf(state, source, entry.instance)?.recordId) return true;
  return planned.has(entryId(source.key, source.multiple ? entry.instance : 0));
};

/**
 * Whether an empty reference may be linked by the form. An insert always may. An existing
 * record only when the field loaded empty: a record (or a shared section's owner record)
 * not loaded yet is unknown, so it may never overwrite a stored reference.
 */
const linkableEmpty = (entry: PlannedEntry, f: EventFieldView): boolean =>
  entry.mode === "insert" ||
  (f.name in entry.state.loaded && isEmpty(entry.state.loaded[f.name]));

/** Whether a submit now would link this empty reference field to its source record. */
export const willLinkOnSave = (
  view: EventView,
  state: EventFormState,
  section: EventSectionView,
  instance: number,
  f: EventFieldView
): boolean => {
  const entries = plan(view, state);
  const entry = entries.find(
    e => e.section.key === section.key && e.instance === instance
  );
  if (!entry || !f.ref) return false;
  if (!isEmpty(fieldValue(f, entry.state.values[f.name]))) return false;
  const planned = new Set(entries.map(e => entryId(e.section.key, e.instance)));
  return linkableEmpty(entry, f) && willLink(view, state, planned, entry, f);
};

export const validateForSubmit = (
  view: EventView,
  state: EventFormState
): Record<string, (ClientError | null)[]> => {
  const errors: Record<string, (ClientError | null)[]> = Object.fromEntries(
    Object.entries(state.sections).map(([key, list]) => [
      key,
      list.map(() => null),
    ])
  );
  const entries = plan(view, state);
  const planned = new Set(entries.map(e => entryId(e.section.key, e.instance)));
  for (const entry of entries) {
    for (const f of entry.section.fields) {
      const v = fieldValue(f, entry.state.values[f.name]);
      if (isEmpty(v)) {
        const linked = willLink(view, state, planned, entry, f);
        const needed =
          entry.mode === "insert" || !isEmpty(entry.state.loaded[f.name]);
        if (f.required && needed && !linked && !isBoolean(f)) {
          errors[entry.section.key][entry.instance] = {
            field: f.name,
            message: REQUIRED_MESSAGE,
          };
          break;
        }
        continue;
      }
      const c = coerce(f, v as FieldValue);
      if (!c.ok) {
        errors[entry.section.key][entry.instance] = {
          field: f.name,
          message: c.message,
        };
        break;
      }
    }
  }
  return errors;
};

/**
 * What an entry's values are compared with, so an unchanged field is not sent: what the
 * instance itself loaded. A shared section with nothing loaded whose owner holds a picked
 * or preselected record compares with its defaults, so it never writes defaults over that
 * stored record. Null means send all its values: an insert, or a shared section whose owner
 * is an insert or was inserted by this form.
 */
const baselineOf = (
  state: EventFormState,
  entry: PlannedEntry
): Record<string, FieldValue> | null => {
  if (entry.mode === "insert") return null;
  if (hasAny(entry.state.loaded)) return entry.state.loaded;
  const ownerSection = entry.section.sharesRecordWith;
  const owner = ownerSection
    ? state.sections[ownerSection]?.[entry.instance]
    : null;
  if (owner?.recordId && !owner.inserted) return defaultsOf(entry.section);
  return null;
};

export const buildSubmitBody = (
  view: EventView,
  state: EventFormState
): EventSubmitRequestBody | null => {
  const entries = plan(view, state);
  if (!entries.length) return null;
  const planned = new Set(entries.map(e => entryId(e.section.key, e.instance)));
  const sections: EventSubmitSection[] = entries.map(entry => {
    const values: Record<string, unknown> = {};
    const baseline = baselineOf(state, entry);
    for (const f of entry.section.fields) {
      const v = fieldValue(f, entry.state.values[f.name]);
      if (isEmpty(v) && f.ref && linkableEmpty(entry, f)) {
        const source = linkSource(view, state, entry.section, f);
        const linked = source
          ? (instanceOf(state, source, entry.instance)?.recordId ?? null)
          : null;
        if (linked) {
          // The server links only to records saved in this submit; a retry knows the id already.
          values[f.name] = linked;
          continue;
        }
        if (
          entry.mode === "update" &&
          willLink(view, state, planned, entry, f)
        ) {
          // Sent empty so the server links the source record saved in this submit.
          values[f.name] = null;
          continue;
        }
      }
      // An update sends only what changed, so a stored value is never rewritten as is.
      if (baseline && v === fieldValue(f, baseline[f.name])) continue;
      if (isEmpty(v)) {
        if (entry.mode === "update" && !isEmpty(entry.state.loaded[f.name])) {
          values[f.name] = null;
        }
        continue;
      }
      const c = coerce(f, v as FieldValue);
      if (c.ok) values[f.name] = c.value;
    }
    const out: EventSubmitSection = {
      section: entry.section.key,
      instance: entry.instance,
      mode: entry.mode,
      values,
    };
    if (
      entry.section.sharesRecordWith === null &&
      entry.mode === "update" &&
      entry.state.recordId
    ) {
      out.recordId = entry.state.recordId;
    }
    return out;
  });
  return { version: state.version, sections };
};
